import _ from "lodash";
import { observable } from "mobx";
import * as React from "react";
import { ButtonTypes } from "../../../components/ui/Button";
import { Icon, IconSymbols } from "../../../components/ui/Icon";
import { Panel } from "../../../components/ui/Panel";
import { IFilterModel } from "../../../core/filter/Filter_model";
import { INIT_CHECKBOX_FIELD } from "../../../core/forms/controls/checkbox/Checkbox_init";
import { generateFormFieldsFromJson } from "../../../core/forms/helpers/FormFieldMappers";
import I18n from "../../../core/localization/I18n";
import { IModalContextModel } from "../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../core/modalZ/context/ModalContext";
import { IUiAction, UiActionRenderers } from "../../../core/uiAction/IUiAction";
import { Animations } from "../../../core/util/Animations";
import { SingleFormModel } from "../../../pages/change/forms/singleFormModel/SingleForm_model";
import { IFlightPathApiResponse } from "../../api/v2/BaseApiModel";
import { IFilteredOptions } from "../../api/v2/filteredApi/FilteredApiModel";
import CsvHelper, { ICsvHelper } from "../csvHelper/CsvHelper";

interface ExportFieldItem<T> extends FP.Generic.IKeyLabel {
  selector?: (obj: T) => React.ReactNode;
  isHidden?: boolean;
  extraColumn?: ExportFieldItem<T>;
}

interface IKeyLabelHidden extends FP.Generic.IKeyLabel {
  isHidden?: boolean;
}

export interface IExportConfig<T> {
  exportCb: (columns: string[], filterOptions: Partial<IFilteredOptions>) => Promise<IFlightPathApiResponse<T[]>>;
  fields: ExportFieldItem<T>[];
  filename: string;
}

export interface IExportService<T> {
  showSelectionModal: () => void;
  setConfig: (config: Partial<IExportConfig<T>>) => void;
}

export class ExportService<T> implements IExportService<T> {
  @observable.ref config: IExportConfig<T>;
  modalService: IModalContextModel;
  filterModel: IFilterModel<T>;
  csvHelper: ICsvHelper;
  /**
   *
   */
  constructor(filterModel: IFilterModel<T>, config?: Partial<IExportConfig<T>>) {
    this.modalService = ModalContext;
    this.csvHelper = CsvHelper;
    this.filterModel = filterModel;
    if (config) {
      this.setConfig(config);
    }
  }

  setConfig = (config: Partial<IExportConfig<T>>) => {
    this.config = { ...this.config, ...config };
  };

  showSelectionModal = () => {
    let formModel = new SingleFormModel();
    formModel.formFields = this.getFormFields(this.config.fields);
    let actions: IUiAction<any>[] = [
      {
        id: "action1",
        label: I18n.t("phrases.cancel"),
        onAction: ev => {
          this.modalService.hide();
        },
        componentProps: {
          type: ButtonTypes.LINK,
          className: "ml-auto"
        },
        rendersIn: UiActionRenderers.BUTTON
      },
      {
        id: "action2",
        label: (
          <>
            <Icon symbol={IconSymbols.Download} className="mr-2" />
            {I18n.t("phrases.export")}
          </>
        ),
        onAction: async ev => {
          let res = await formModel.submit();
          if (!res) return;

          let keys = Object.keys(res);
          let columns = [];
          let headerFields = [];
          keys.forEach(k => {
            if (res[k]) {
              columns.push(k);
              let s = this.config.fields.find(e => e.key === k);
              if (s) {
                headerFields.push(s);
              }
            }
          });
          let result = await this.config.exportCb(columns, this.filterModel.getFilterOptions());

          if (!result || result.isError) return;

          this.generateCsv(result.payload, headerFields);
        },
        componentProps: {
          className: "ml-2"
        },
        rendersIn: UiActionRenderers.BUTTON
      }
    ];
    formModel.actions = actions;
    return new Promise(resolve => {
      this.modalService.show({
        showClose: true,
        title: <h1 className="mt-6">{I18n.t("phrases.selectExportFields")}</h1>,
        content: <div className="container-fluid">{formModel.renderComponent()}</div>,
        componentProps: {
          wrapHeight: "full",
          wrapWidth: "small",
          position: "right",
          panelProps: {
            background: Panel.PanelBackgrounds.BG_WHITE
          }
        },
        animationOptions: {
          animateIn: Animations.SLIDE_IN_RIGHT,
          animateOut: Animations.SLIDE_OUT_RIGHT,
          speed: 5
        },
        actions: []
      });
    });
  };

  generateCsv = (data: T[], fields: ExportFieldItem<T>[]) => {
    let headers = [
      fields.map(e => {
        const supportCol = e.extraColumn && e.extraColumn.label;
        return `${e.label}${supportCol ? `,${_.startCase(I18n.t(supportCol))}` : ""}`;
      })
    ];

    let res = data.map(row => {
      return fields.map(header => {
        let extraColumnContent: any = this.getExtraColumnContent(header, row);
        if (header.selector) {
          return header.selector(row) + extraColumnContent;
        }
        return row[header.key] + extraColumnContent;
      });
    });

    let combined = [...headers, ...res];
    this.csvHelper.exportToCsv(this.config.filename, combined);
  };

  getExtraColumnContent = (header: ExportFieldItem<T>, row: T) => {
    let extraColumnContent: any = "";
    if (header.extraColumn) {
      if (header.extraColumn.selector) {
        extraColumnContent = "," + header.extraColumn.selector(row);
      } else {
        extraColumnContent = "," + row[header.extraColumn.key];
      }
    }
    return extraColumnContent;
  };

  getFormFields = (fields: IKeyLabelHidden[]) => {
    let res = fields.map(e => {
      return {
        ...INIT_CHECKBOX_FIELD,
        key: e.key,
        fieldClassName: "col-12 d-flex flex-row-reverse justify-content-end",
        label: <label htmlFor={e.key}>{e.label} </label>,
        value: true,
        isHidden: e.isHidden
      };
    });
    return generateFormFieldsFromJson(res);
  };
}
